import { useCallback, useState } from "react";
import type { AgentReading } from "./useHubReadings";
import { overviewPanels, panelHref } from "../lib/workspaces";
import { seatWords } from "../studio/words";
import { agentHref, connectionNotice, everConnected, instanceStatus, wakesOnDemand, type AgentInstance, type CreatedAgent } from "./api";
import { Icon, declaredIcon } from "../components/Icon";
import { Avatar } from "../shell/Avatar";
import { OwnerEmpty } from "../shell/OwnerEmpty";
import { OwnerSettings } from "./OwnerSettings";
import { FirstWorkspace } from "./FirstWorkspace";
import { ownerAsk } from "../studio/ask";
import { OverviewPanels, type OverviewCard } from "./OverviewPanels";
import { OwnerResponses, pendingInteractions, requestHref } from "./OwnerResponses";
import { PageHead } from "../shell/PageHead";
import { Toast, type ToastMessage } from "./Toast";
import { useFirstReading } from "./useFirstReading";
import type { OverviewReply } from "./RecentReplies";
import { responseConversationHref } from "../shell/ownerShell";
import "./contacts.css";

export function OwnerHubPages({ page, hash = "", agents, accountName, onLogout, onAgentUpdated, onAgentDeleted, pairingReceipts, onPairingIssued, readings, onRefresh }: {
  page: "overview" | "people" | "responses" | "settings"; agents: AgentInstance[]; accountName: string;
  /** The Hub's address, which 设置 opens at. */
  hash?: string;
  readings: Record<string, AgentReading>;
  /** Reads the Agents again: what an answer calls once it is sent, and what a failed read's 重试 calls. */
  onRefresh: () => void;
  onLogout?: () => void;
  onAgentUpdated?: (agent: AgentInstance) => void;
  onAgentDeleted?: (id: string) => void;
  pairingReceipts?: Record<string, CreatedAgent>;
  onPairingIssued?: (receipt: CreatedAgent) => void;
}) {
  const [notice, setNotice] = useState<ToastMessage | null>(null);
  const dismiss = useCallback(() => setNotice(null), []);
  // Whether the Agents' first reads are all back. 待我回应 draws its groups once then, in the order
  // they will stay in, so a group is not pushed down or moved up by the one that arrives after it.
  const firstRead = useFirstReading(agents.some(agent => {
    const reading = readings[agent.id];
    return agent.online && (!reading || reading.state === "reading" || reading.approvalsState === "reading" || reading.choicesState === "reading");
  }));
  if (page === "settings") return <OwnerSettings hash={hash} agents={agents} accountName={accountName} onLogout={onLogout}
    onAgentUpdated={onAgentUpdated} onAgentDeleted={onAgentDeleted} pairingReceipts={pairingReceipts} onPairingIssued={onPairingIssued} />;
  if (page === "overview" && !agents.length) return <FirstWorkspace accountName={accountName} />;
  if (page === "overview") return <OverviewPanels cards={overviewCards(agents, readings)} accountName={accountName}
    replies={overviewReplies(agents, readings)}
    agents={agents.map(agent => {
      const reading = readings[agent.id];
      return { id: agent.id, name: agent.name, status: instanceStatus(agent).label, online: agent.online,
        href: agentHref(agent.id, "#/today"),
        loading: agent.online && (!reading || reading.state === "reading"),
        notice: !agent.online ? connectionNotice(agent)?.text
          : reading?.state === "failed" ? "工作界面暂未读到"
          : reading?.state === "ready" && !reading.boot?.owner?.present ? "管理身份尚未确认"
          : reading?.approvalsState === "failed" ? "待回应事项暂未读到" : undefined,
        noticeAction: agent.online ? undefined : connectionNotice(agent)?.action,
      };
    })}
    pending={agents.some(agent => agent.online && (!readings[agent.id] || readings[agent.id].state === "reading" || readings[agent.id].previewState === "reading" || readings[agent.id].approvalsState === "reading"))}
    // Unreadable, and only that.  An Agent that has never connected has no
    // records this page failed to read, and counting it here is how the
    // overview came to say 「工作记录暂未读完整」 to an Owner whose one Agent
    // had not been paired yet -- a fault report about an Agent that has never
    // had anything to read.
    unavailable={agents.filter(agent => (!agent.online && !wakesOnDemand(agent) && everConnected(agent)) || readings[agent.id]?.state === "failed" || readings[agent.id]?.previewState === "failed" || readings[agent.id]?.approvalsState === "failed" || readings[agent.id]?.repliesState === "failed").map(agent => agent.id)}
    unconnected={agents.filter(agent => !agent.online && !wakesOnDemand(agent) && !everConnected(agent)).map(agent => agent.id)} />;
  if (!agents.length) return <div className={`owner-page owner-hub-${page}`}>
    <PageHead title={page === "people" ? "联系人" : "待我回应"} />
    <OwnerEmpty icon={page === "people" ? "people" : "inbox"} action={<a className="btn btn-accent" href="/#/create"><Icon name="plus" size={16} />新建 IP</a>}>
      {page === "people" ? "建好第一个 IP 后，和它说话的人会出现在这里。" : "建好第一个 IP 后，需要你回答的事会出现在这里。"}</OwnerEmpty>
  </div>;
  if (page === "responses") return <div className="owner-page owner-hub-responses">
    <PageHead title="待我回应" meta="你的 IP 需要你确认、选择或补充时，会列在这里。" />
    {firstRead
      ? <OwnerResponses agents={agents} readings={readings} onRefresh={onRefresh} onToast={(message, tone) => setNotice({ message, tone })} />
      : <p className="owner-read-notice" role="status">正在读取待回应的事项…</p>}
    <Toast toast={notice} onDismiss={dismiss} />
  </div>;
  return <div className="owner-page owner-hub-people">
    <PageHead title="联系人" meta="和你的 IP 说话的人，以及他们用的账号。" />
    <div className="owner-contact-list">{agents.map(agent => {
      const reading = readings[agent.id];
      const state = reading?.state;
      const people = reading?.boot?.principals.filter(person => !person.revoked) ?? [];
      return <section className="owner-card owner-contacts" key={agent.id} aria-label={agent.name || "未命名 IP"}>
        <header>
          <a href={agentHref(agent.id)}><Avatar name={agent.name || "未命名 IP"} identity={agent.id} size="lg" /><h2 data-quoted="true">{agent.name || "未命名 IP"}</h2></a>
          <span className="micro">{instanceStatus(agent).label}</span>
          <a className="btn btn-quiet" href={agentHref(agent.id, "#/ip")}>查看身份与关联账号<Icon name="arrow" size={15} /></a>
        </header>
        {state !== "ready" ? <ReadNotice state={state} onRetry={onRefresh} /> : null}
        {people.length ? <ul>{people.map(person => <li className="owner-person" key={person.id}>
          <Avatar name={person.display_name || (person.role === "owner" ? accountName : "")} size="md" glyph="person" />
          <strong data-quoted="true">{person.display_name || (person.role === "owner" ? accountName : "未命名联系人")}</strong>
          <span>{person.role === "owner" ? "你本人" : "联系人"}</span>
          <span>{seatWords(person.channels, reading?.channels).join(" · ")}</span>
        </li>)}</ul> : null}
        {state === "ready" && !people.length ? <p className="owner-read-notice">尚未关联联系人。</p> : null}
      </section>;
    })}</div>
  </div>;
}

function ReadNotice({ state, onRetry }: { state?: AgentReading["state"]; onRetry: () => void }) {
  return <p className="owner-read-notice" role="status">{state === "sleeping" ? "打开这个 IP 后会自动唤醒，再读取最新工作。"
    : state === "offline" ? "这个 IP 离线，尚未读取这次的工作状态。" : state === "failed" ? "暂时没有读到完整状态。" : "正在读取最新状态…"}
    {state === "failed" ? <button type="button" className="btn btn-quiet" onClick={onRetry}>重试</button> : null}</p>;
}

function overviewCards(agents: AgentInstance[], readings: Record<string, AgentReading>): OverviewCard[] {
  return agents.flatMap(agent => {
    const reading = readings[agent.id];
    if (!agent.online || reading?.state !== "ready" || !reading.boot?.owner?.present) return [];
    const panels = reading.boot.panels ?? [];
    const cards: OverviewCard[] = overviewPanels(panels).flatMap(panel => {
      const preview = reading.previews?.[panel.key];
      if (panel.audience !== "owner" || !preview || preview.generation_id !== panel.generation_id) return [];
      return preview.items.filter(item => item.id && item.title && ["working", "done", "action"].includes(item.state)).map(item => ({ ...item,
        // An item that named its picture and has none yet (`null`) is not given a screenshot of the page in its place.
        thumbnail: item.thumbnail !== undefined ? item.thumbnail : preview.workspace_thumbnail,
        agentId: agent.id, agentName: agent.name, panelKey: panel.key, generation: panel.generation_id, icon: declaredIcon(panel.icon),
        href: agentHref(agent.id, panelHref(panel.key, item.context)) }));
    });
    for (const panel of pendingInteractions(reading)) {
      cards.push({ id: `request:${panel.interaction!.request_id}`, title: panel.title, state: "action", status: "等待你回应", context: {},
        agentId: agent.id, agentName: agent.name, panelKey: panel.key, generation: panel.generation_id, icon: declaredIcon(panel.icon, "waiting"),
        href: requestHref(agent, panel), action_label: "打开请求" });
    }
    if (reading.approvalsState === "ready") for (const request of reading.pending ?? []) {
      cards.push({ id: `approval:${request.key}`, title: ownerAsk(request, reading.tools ?? {}).title, state: "action", status: "待确认", context: {},
        agentId: agent.id, agentName: agent.name, panelKey: "", icon: "waiting",
        href: agentHref(agent.id, responseConversationHref(request, { approval: request.key })), action_label: "查看请求" });
    }
    return cards;
  });
}

function overviewReplies(agents: AgentInstance[], readings: Record<string, AgentReading>): OverviewReply[] {
  return agents.flatMap(agent => {
    const reading = readings[agent.id];
    if (!agent.online || reading?.state !== "ready" || !reading.boot?.owner?.present) return [];
    return (reading.replies ?? []).map(reply => ({ ...reply, agentId: agent.id, agentName: agent.name,
      href: agentHref(agent.id, `#/chat?session=${encodeURIComponent(reply.sessionId)}`) }));
  }).sort((left, right) => right.at - left.at);
}
